import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Send, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../lib/api";

const KIND_LABELS = {
  auto: { label: "Automática", color: "bg-rose-50 text-rose-700 border-rose-200" },
  manual: { label: "Manual", color: "bg-amber-50 text-amber-700 border-amber-200" },
  test: { label: "Teste", color: "bg-sky-50 text-sky-700 border-sky-200" },
};

function formatRelative(iso) {
  if (!iso) return ", ";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function CrisisNotifySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/crisis/settings");
      setSettings(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar configurações de crise.");
    } finally { setLoading(false); }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const r = await api.get("/crisis/notifications");
      setLogs(r.data.items || []);
    } catch (e) {
      // silent, log section is optional
    } finally { setLogsLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); loadLogs(); }, [loadSettings, loadLogs]);

  const toggleEnabled = async () => {
    if (saving || !settings) return;
    setSaving(true);
    try {
      const r = await api.put("/crisis/settings", { enabled: !settings.enabled });
      setSettings(r.data);
      toast.success(r.data.enabled ? "Alertas ativados." : "Alertas pausados.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível atualizar.");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await api.post("/crisis/test-notification");
      if (r.data.sent) {
        toast.success(`Teste enviado ao WhatsApp do pastor (${r.data.pastor_phone_masked}). SID ${r.data.twilio_sid?.slice(-6)}.`);
      } else {
        toast.error(r.data.error || "Falha no envio do teste.");
      }
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao disparar o teste.");
    } finally { setTesting(false); }
  };

  return (
    <section data-testid="crisis-notify-settings" className="bg-white border border-border rounded-2xl p-5 sm:p-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-rose-600 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Alertas de crise
          </p>
          <h3 className="font-display text-xl text-navy mt-1">Notificação WhatsApp do pastor</h3>
          <p className="text-[12px] text-navy/55 mt-1.5 max-w-md">
            Quando a IA classificar uma mensagem como <strong>CRISE</strong>, envia um alerta imediato
            para o WhatsApp pessoal do pastor com nome, telefone e a mensagem original.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-navy/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…
        </div>
      ) : !settings ? null : (
        <div className="space-y-4">
          {/* Status row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoCell
              label="Pastor"
              value={settings.pastor_phone_masked || ", "}
              ok={settings.pastor_phone_configured}
              data-testid="crisis-pastor-phone"
            />
            <InfoCell
              label="Twilio WhatsApp"
              value={settings.twilio_ready ? "Conectado" : "Não configurado"}
              ok={settings.twilio_ready}
              data-testid="crisis-twilio-ready"
            />
          </div>

          {/* Toggle + test */}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
            <button
              type="button"
              role="switch"
              aria-checked={settings.enabled}
              data-testid="crisis-notify-toggle"
              disabled={saving}
              onClick={toggleEnabled}
              className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors ${
                settings.enabled ? "bg-rose-600" : "bg-navy/20"
              } disabled:opacity-50`}
            >
              <span
                className={`inline-block w-5 h-5 bg-white rounded-full transition-transform shadow ${
                  settings.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <p className="text-sm text-navy flex-1 min-w-[160px]">
              {settings.enabled ? (
                <span><strong className="text-rose-600">Ativo:</strong> alertas de crise enviam ao WhatsApp do pastor.</span>
              ) : (
                <span className="text-navy/60"><strong>Pausado:</strong> nenhum alerta WhatsApp será enviado.</span>
              )}
            </p>
            <button
              type="button"
              onClick={sendTest}
              disabled={testing || !settings.twilio_ready || !settings.pastor_phone_configured}
              data-testid="crisis-send-test-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white text-[12px] uppercase tracking-widest hover:bg-rose-600 disabled:opacity-50 transition-colors"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Enviar teste
            </button>
          </div>

          {/* Recent log */}
          <div className="border-t border-border pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Últimas notificações</p>
              <button
                type="button"
                onClick={loadLogs}
                disabled={logsLoading}
                data-testid="crisis-notify-refresh-log"
                className="inline-flex items-center gap-1.5 text-[11px] text-navy/55 hover:text-navy"
              >
                <RefreshCw className={`w-3 h-3 ${logsLoading ? "animate-spin" : ""}`} /> Atualizar
              </button>
            </div>
            {logsLoading ? (
              <div className="text-center text-navy/45 py-4 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />carregando log…
              </div>
            ) : logs.length === 0 ? (
              <p className="text-sm text-navy/55 py-2">Nenhuma notificação enviada ainda.</p>
            ) : (
              <ul className="divide-y divide-border" data-testid="crisis-notify-log">
                {logs.slice(0, 8).map((row) => {
                  const meta = KIND_LABELS[row.kind] || KIND_LABELS.auto;
                  const ok = row.status === "sent";
                  return (
                    <li key={row.id} className="py-2.5 flex items-start gap-3">
                      <div className="pt-0.5">
                        {ok ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : row.status === "skipped" ? (
                          <Clock className="w-4 h-4 text-navy/40" />
                        ) : (
                          <XCircle className="w-4 h-4 text-rose-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-navy truncate">
                          <span className={`inline-block text-[10px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 mr-1.5 ${meta.color}`}>
                            {meta.label}
                          </span>
                          {row.user_name || row.user_phone || ", "}
                        </p>
                        <p className="text-[11px] text-navy/55 truncate">
                          {ok ? `SID ${row.twilio_sid?.slice(-8) || ", "}` : (row.error || row.status)}
                        </p>
                      </div>
                      <span className="text-[10px] text-navy/45 whitespace-nowrap pt-1">{formatRelative(row.created_at)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function InfoCell({ label, value, ok, ...props }) {
  return (
    <div {...props} className="bg-[hsl(var(--parchment))] border border-border rounded-xl px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-widest text-navy/50">{label}</p>
      <p className={`text-sm font-medium mt-0.5 truncate flex items-center gap-1.5 ${ok ? "text-navy" : "text-rose-700"}`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />}
        <span className="truncate">{value}</span>
      </p>
    </div>
  );
}
